import i18n from 'i18n-js';
import * as types from './types';
import { uiActions } from 'state/modules/UI';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import * as reservationsActions from 'services/api/reservations';
import * as invoiceItemAPI from 'services/api/invoiceItem';

// Get reservations
const fetchReservationsRequest = () => ({
  type: types.FETCH_RESERVATIONS_REQUEST,
});

const fetchReservationsSuccess = (payload) => ({
  type: types.FETCH_RESERVATIONS_SUCCESS,
  payload,
});

const fetchReservationsFailed = () => ({
  type: types.FETCH_RESERVATIONS_FAILED,
});

export const fetchReservations = (params = {}, options) => {
  return async (dispatch) => {
    dispatch(fetchReservationsRequest());

    const { invoice_item_id, ...rest } = params;

    let response;

    if (invoice_item_id) {
      response = await invoiceItemAPI.fetchInvoicesItemReservations(invoice_item_id, rest, options);
    } else {
      response = await reservationsActions.fetchReservations(params, options);
    }

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchReservationsSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    if (!isCancel(response)) {
      const error = getErrorMessage(response.data);

      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );

      dispatch(fetchReservationsFailed());
    }
  };
};

// Get reservation by ID
const fetchReservationByIdRequest = () => ({
  type: types.FETCH_RESERVATION_BY_ID_REQUEST,
});

const fetchReservationByIdSuccess = (payload) => ({
  type: types.FETCH_RESERVATION_BY_ID_SUCCESS,
  payload,
});

const fetchReservationByIdFailed = () => ({
  type: types.FETCH_RESERVATION_BY_ID_FAILED,
});

export const fetchReservationById = (id) => {
  return async (dispatch) => {
    dispatch(fetchReservationByIdRequest());

    const response = await reservationsActions.fetchReservationById(id);

    if (response.ok) {
      return dispatch(fetchReservationByIdSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchReservationByIdFailed());
  };
};

// Create reservation
const fetchCreateReservationRequest = () => ({
  type: types.FETCH_CREATE_RESERVATION_REQUEST,
});

const fetchCreateReservationSuccess = (payload) => ({
  type: types.FETCH_CREATE_RESERVATION_SUCCESS,
  payload,
});

const fetchCreateReservationFailed = () => ({
  type: types.FETCH_CREATE_RESERVATION_FAILED,
});

export const createReservation = (payload) => {
  return async (dispatch) => {
    dispatch(fetchCreateReservationRequest());

    const response = await reservationsActions.createReservation(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('reservations.reservation_created'),
        })
      );
      return dispatch(fetchCreateReservationSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchCreateReservationFailed());
  };
};

const fetchUpdateReservationRequest = () => ({
  type: types.FETCH_UPDATE_RESERVATION_REQUEST,
});

const fetchUpdateReservationSuccess = (payload) => ({
  type: types.FETCH_UPDATE_RESERVATION_SUCCESS,
  payload,
});

const fetchUpdateReservationFailed = () => ({
  type: types.FETCH_UPDATE_RESERVATION_FAILED,
});

export const updateReservation = (id, payload) => {
  return async (dispatch) => {
    dispatch(fetchUpdateReservationRequest());

    const response = await reservationsActions.updateReservation(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('reservations.reservation_updated'),
        })
      );
      return dispatch(fetchUpdateReservationSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      dispatch(fetchUpdateReservationFailed());
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchUpdateReservationFailed({ errors }));
  };
};

// Get reservations rules
const fetchReservationsRulesRequest = () => ({
  type: types.FETCH_RESERVATIONS_RULES_REQUEST,
});

const fetchReservationsRulesSuccess = (payload) => ({
  type: types.FETCH_RESERVATIONS_RULES_SUCCESS,
  payload,
});

const fetchReservationsRulesFailed = () => ({
  type: types.FETCH_RESERVATIONS_RULES_FAILED,
});

export const fetchReservationRules = (params) => {
  return async (dispatch) => {
    dispatch(fetchReservationsRulesRequest());

    const response = await reservationsActions.fetchReservationRules(params);

    if (response.ok) {
      return dispatch(
        fetchReservationsRulesSuccess({
          data: response.data,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchReservationsRulesFailed());
  };
};

// Start reservation
const startReservationRequest = () => ({
  type: types.START_RESERVATION_REQUEST,
});

const startReservationSuccess = (payload) => ({
  type: types.START_RESERVATION_SUCCESS,
  payload,
});

const startReservationFailed = () => ({
  type: types.START_RESERVATION_FAILED,
});

export const startReservation = (id) => {
  return async (dispatch) => {
    dispatch(startReservationRequest());

    const response = await reservationsActions.startReservation(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('reservations.reservation_started'),
        })
      );
      return dispatch(startReservationSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(startReservationFailed());
  };
};

// End reservation
const endReservationRequest = () => ({
  type: types.END_RESERVATION_REQUEST,
});

const endReservationSuccess = (payload) => ({
  type: types.END_RESERVATION_SUCCESS,
  payload,
});

const endReservationFailed = () => ({
  type: types.END_RESERVATION_FAILED,
});

export const endReservation = (id) => {
  return async (dispatch) => {
    dispatch(endReservationRequest());

    const response = await reservationsActions.endReservation(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('reservations.reservation_ended'),
        })
      );
      return dispatch(endReservationSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(endReservationFailed());
  };
};

// GCancel reservation
const cancelReservationRequest = () => ({
  type: types.CANCEL_RESERVATION_REQUEST,
});

const cancelReservationSuccess = (payload) => ({
  type: types.CANCEL_RESERVATION_SUCCESS,
  payload,
});

const cancelReservationFailed = () => ({
  type: types.CANCEL_RESERVATION_FAILED,
});

export const cancelReservation = (id, payload) => {
  return async (dispatch) => {
    dispatch(cancelReservationRequest());

    const response = await reservationsActions.cancelReservation(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('reservations.reservation_canceled'),
        })
      );
      return dispatch(cancelReservationSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(cancelReservationFailed());
  };
};

export const resetUpdateReservation = () => ({
  type: types.RESET_UPDATE_RESERVATION,
});

const fetchAddBrokerRequest = (payload) => ({
  type: types.FETCH_ADD_BROKER_REQUEST,
  payload,
});

const fetchAddBrokerSuccess = (payload) => ({
  type: types.FETCH_ADD_BROKER_SUCCESS,
  payload,
});

const fetchAddBrokerFailed = (payload) => ({
  type: types.FETCH_ADD_BROKER_FAILED,
  payload,
});

export const fetchAddBroker = (id, broker) => async (dispatch) => {
  dispatch(fetchAddBrokerRequest());

  const response = await reservationsActions.addBrokerToReservation(id, { broker });

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('reservations.broker_add_success'),
      })
    );
    dispatch(fetchAddBrokerSuccess(response.data));
    return dispatch(fetchReservationById(id));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchAddBrokerFailed({ errors: formattedError }));
};

const fetchRemoveBrokerRequest = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_REQUEST,
  payload,
});

const fetchRemoveBrokerSuccess = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_SUCCESS,
  payload,
});

const fetchRemoveBrokerFailed = (payload) => ({
  type: types.FETCH_REMOVE_BROKER_FAILED,
  payload,
});

export const fetchRemoveBroker = (id, broker) => async (dispatch) => {
  dispatch(fetchRemoveBrokerRequest());

  const response = await reservationsActions.removeBrokerFromReservations(id, broker);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('reservations.broker_remove_success'),
      })
    );
    dispatch(
      fetchRemoveBrokerSuccess({
        data: response.data,
      })
    );
    return dispatch(fetchReservationById(id));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchRemoveBrokerFailed({ errors: formattedError }));
};

// GET BUSINESS HOURS
const fetchBusinessHoursRequest = () => ({
  type: types.FETCH_BUSINESS_HOURS_REQUEST,
});

const fetchBusinessHoursSuccess = (payload) => ({
  type: types.FETCH_BUSINESS_HOURS_SUCCESS,
  payload,
});

const fetchBusinessHoursFailed = (errors) => ({
  type: types.FETCH_BUSINESS_HOURS_FAILED,
  errors,
});

export const fetchBusinessHours = (params, options) => {
  return async (dispatch) => {
    dispatch(fetchBusinessHoursRequest());

    const response = await reservationsActions.fetchBusinessHours(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchBusinessHoursSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    if (!isCancel(response)) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: getErrorMessage(response.data),
        })
      );
      dispatch(fetchBusinessHoursFailed(response.data));
    }
  };
};

// Reservation Bulk Action
const fetchBulkActionReservationsRequest = () => ({
  type: types.FETCH_BULK_ACTION_RESERVATIONS_REQUEST,
});

const fetchBulkActionReservationsSuccess = (payload) => ({
  type: types.FETCH_BULK_ACTION_RESERVATIONS_SUCCESS,
  payload,
});

const fetchBulkActionReservationsFailed = (error) => ({
  type: types.FETCH_BULK_ACTION_RESERVATIONS_FAILED,
  error,
});

export const bulkActionReservations = (params) => {
  return async (dispatch) => {
    dispatch(fetchBulkActionReservationsRequest());
    try {
      const response = await reservationsActions.bulkActionReservations(params);
      if (response.ok) {
        dispatch(
          uiActions.handleShowSnackbar({
            type: 'success',
            message: i18n.t('reservations.bulk_action_success'),
          })
        );
        return dispatch(fetchBulkActionReservationsSuccess(response.data));
      }
      const error = getErrorMessage(response.data);
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );
      dispatch(fetchBulkActionReservationsFailed(error));
    } catch (err) {
      dispatch(fetchBulkActionReservationsFailed(err.message));
    }
  };
};

export const changeBulkReservationsStatusRequest = () => ({
  type: types.CHANGE_BULK_RESERVATIONS_STATUS_REQUEST,
});

export const changeBulkReservationsStatusSuccess = (payload) => ({
  type: types.CHANGE_BULK_RESERVATIONS_STATUS_SUCCESS,
  payload,
});

export const changeBulkReservationsStatusFailed = (errors) => ({
  type: types.CHANGE_BULK_RESERVATIONS_STATUS_FAILED,
  errors,
});

export const changeBulkReservationsStatus = (payload) => {
  return async (dispatch) => {
    dispatch(changeBulkReservationsStatusRequest());

    const response = await reservationsActions.bulkActionReservations(payload);

    if (response.ok) {
      return dispatch(changeBulkReservationsStatusSuccess(response.data));
    }
    const { data } = response;
    dispatch(changeBulkReservationsStatusFailed(data));
  };
};

export const resetBulkReservationsStatus = () => ({
  type: types.RESET_BULK_RESERVATIONS_STATUS,
});
